import { service } from '@ember/service';
import moment from 'moment-timezone';
import eflexFeatures from 'eflex/constants/eflex-features';
import { isEmpty, isPresent } from '@ember/utils';
import { camelize } from '@ember/string';
import Model, { attr } from '@ember-data/model';
import { objectAttr, arrayAttr } from 'eflex/decorators';

const getExpirationMoments = function(expirationDate, timezone) {
  const expirationDateMoment = moment(expirationDate);
  const now = moment();
  if (timezone != null) {
    expirationDateMoment.tz(timezone);
    now.tz(timezone);
  }
  expirationDateMoment.endOf('day');

  return { expirationDateMoment, now };
};

export default class License extends Model {
  @attr('date') expirationDate;
  @attr('string') jobNumber;
  @attr('string') label;
  @attr('number') licenseId;
  @attr('number') stationLimit;
  @arrayAttr features;
  @objectAttr organization;

  @service stationRepo;
  @service areaRepo;
  @service systemConfig;

  get expirationThreshold() {
    return this.organization?.expirationThreshold;
  }

  get visionOrImageCaptureEnabled() {
    return this.evisionEnabled || this.imageCaptureEnabled;
  }

  get bomManagerEnabled() {
    return this.useComponentAreas;
  }

  get operationsEnabled() {
    return this.areaRepo.areas.some(area => area.usesOperations);
  }

  get visionEnabled() {
    return this.evisionEnabled;
  }

  get visionDisabled() {
    return this.evisionDisabled;
  }

  get daysTillExpired() {
    const timezone = this.systemConfig.timezone;
    const { expirationDateMoment, now } = getExpirationMoments(this.expirationDate, timezone);

    return expirationDateMoment.diff(now, 'days') + 1;
  }

  get licenseWarningEnabled() {
    const timezone = this.systemConfig.timezone;
    const { expirationDateMoment, now } = getExpirationMoments(this.expirationDate, timezone);

    return expirationDateMoment
      .subtract(this.expirationThreshold, 'days')
      .isBefore(now);
  }

  get activeStations() {
    return this.stationRepo.stations
      .filter(item => !item.isDeleted)
      .filter(item => !item.hideFromOperator)
      .length;
  }

  get atStationLimit() {
    return this.activeStations >= this.stationLimit;
  }

  get aboveStationLimit() {
    return this.activeStations > this.stationLimit;
  }

  get unlicensed() {
    return isEmpty(this.licenseId) ||
      isEmpty(this.stationLimit) ||
      isEmpty(this.features) ||
      isEmpty(this.expirationDate);
  }

  get useComponentAreas() {
    return this.areaRepo.areas.some(item => item.usesComponents) ?? false;
  }

  get disabledHardware() {
    return eflexFeatures
      .filter(feature =>
        isPresent(feature.hardware) && !this.features?.find(item => item.key === feature.key)?.enabled,
      )
      .flatMap(feature => feature.hardware);
  }

  constructor() {
    super(...arguments);

    eflexFeatures.forEach(feature => {
      const featureName = camelize(feature.key);

      Object.defineProperty(this, `${featureName}Licensed`, {
        get() {
          return this.features?.find(item => item.key === feature.key)?.enabled ?? false;
        },
      });

      Object.defineProperty(this, `${featureName}Disabled`, {
        get() {
          return !this[`${featureName}Enabled`];
        },
      });

      Object.defineProperty(this, `${featureName}Enabled`, {
        get() {
          const timezone = this.systemConfig.timezone;
          const { expirationDateMoment, now } = getExpirationMoments(this.expirationDate, timezone);

          return (
            this[`${featureName}Licensed`] && !expirationDateMoment.isBefore(now)
          );
        },
      });

      Object.defineProperty(this, `${featureName}LicenseWarningEnabled`, {
        get() {
          return this[`${featureName}Licensed`] && this.licenseWarningEnabled;
        },
      });
    });

    Object.defineProperty(this, 'noProductsLicensed', {
      get() {
        return eflexFeatures.every(feature => this[`${camelize(feature.key)}Disabled`]);
      },
    });
  }
}
